import React from 'react';
import {useHistory, useLocation} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import useSWR from 'swr';
import {get, find, isEmpty} from 'lodash';
import {useForm, Controller} from 'react-hook-form';
import api, {getURL} from 'api';
import {format} from 'date-fns';
import {useComputedReservationDetails} from 'context/computedReservationDetails';
import Page from 'components/acommon/Page';
import StyledIcon from 'components/acommon/StyledIcon';
import Input from 'components/acommon/StyledInput';
import Select from 'components/acommon/StyledSelect';
import {getDocTypes} from 'utils/docTypes';
import {PATHS} from 'Routes';

import {GENDERS_OPTIONS} from 'utils/constants';
import Datepicker from 'components/Datepicker';
import ContractModal from './ContractModal';
import Button, {DesktopBackButton} from 'components/Button';
import {
  StyledPage,
  BackButtonMenu,
  HeaderEditGuest,
  HeaderEditGuest2,
  StyledForm,
  Row,
  Col,
  GuestName,
  Signature,
} from './styled';
import {getErrorMessage} from 'utils/common';

type LocationTypes = {
  guestId: string;
  hash?: string;
  key?: string;
  pathname?: string;
  search?: string;
  state?: any;
};

type GuestDataTypes = {
  id?: string;
  name?: string;
  surname?: string;
  document?: {front_side_scan?: string; back_side_scan?: string};
  biomatch_selfie?: string;
  biomatch_doc?: string;
  gender?: string;
  pathname?: string;
  search?: string;
  state?: any;
  errors?: any;
  signature?: string;
};

export enum Side {
  front = 'front',
  back = 'back',
  selfie = 'selfie',
}
export type SideType = Side.front | Side.back | Side.selfie;

type LocationType = {
  country: {
    code: string;
    name: string;
  };
};
function getLocationsAsOptions(data: {results: Array<LocationType>}) {
  const results = data?.results;

  if (!results) {
    return [];
  }

  return results.map(l => {
    return {
      value: l?.country?.code,
      label: l?.country?.name,
    };
  });
}

type DocumentPhotoProps = {
  side: SideType;
  connectedStatus: string;
  photo?: string;
  guestId?: string;
};

const getModalLocation = (side: SideType) => {
  if (side === Side.front) return PATHS.verificationDocumentFront;
  if (side === Side.back) return PATHS.verificationDocumentBack;
  return PATHS.verificationSelfie;
};

export function DocumentPhoto(props: DocumentPhotoProps) {
  const history = useHistory();
  const {t} = useTranslation();
  const location = useLocation<Record<string, unknown>>();
  const {photo, side, connectedStatus, guestId} = props;

  const handleClickRetry = React.useCallback(
    (guestId?: string) => {
      const locationState = {...location.state, isRetryBiomatch: true, guestId};
      history.push(getModalLocation(side), locationState);
    },
    [history, location.state, side],
  );

  return (
    <Col>
      <Row className="photoHeader">
        <Col>
          <Row className="photoSide">{t(side)}</Row>
          <Row className="photoConnected" style={{display: 'none'}}>
            {connectedStatus}
          </Row>
        </Col>
        <Col className="retakeButton">
          <Button
            label={t('retake')}
            whiteButton
            onClick={() => handleClickRetry(guestId)}
          />
        </Col>
      </Row>
      <Row>
        {!isEmpty(photo) ? (
          <div>
            <img src={photo} alt={side} width="315" />
          </div>
        ) : (
          <div>{t('no_photo')}</div>
        )}
      </Row>
    </Col>
  );
}

function EditRegistredGuest() {
  const {t} = useTranslation();
  const history = useHistory();
  const location = useLocation<LocationTypes>();
  const reservationDetails = useComputedReservationDetails();

  const [guestData, setGuestData] = React.useState<GuestDataTypes>({});
  const [isOpenModal, setIsOpenModal] = React.useState<boolean>(false);

  const guestId = location?.state?.guestId;
  const fronSideDocumentPicture =
    guestData?.document?.front_side_scan || guestData?.biomatch_doc;
  const backSideDocumentPicture = guestData?.document?.back_side_scan;
  const selfiePicture = guestData?.biomatch_selfie;
  const obj = get(reservationDetails, 'reservation.guest_group.members');
  const isIdentityVerificationEnabled = get(
    reservationDetails,
    'reservation.housing.is_self_online_check_in_enabled',
  );
  const {data: locations} = useSWR(getURL(api.locations.ENDPOINTS.get('ordering=name')));

  const locationsAsOptions = React.useMemo(() => {
    return getLocationsAsOptions(locations);
  }, [locations]);

  const handleContractButton = () => {
    setIsOpenModal(!isOpenModal);
  };

  const {register, reset, handleSubmit, setValue, control} = useForm<any>({
    mode: 'onBlur',
    defaultValues: {
      firstName: guestData?.name,
      lastName: guestData?.surname,
      gender: guestData?.gender,
    },
  });

  const onSubmit = async (data: any) => {
    const payload = {
      name: data.firstName,
      surname: data.lastName,
      birth_date: format(new Date(data.birthDate), 'yyyy-MM-dd'),
      document: {
        issue_date: format(new Date(data.docDateOfIssue), 'yyyy-MM-dd'),
        number: data.docNumber,
        type: data.docType?.value,
      },
      nationality: data.nationality?.value,
      gender: data.sex?.value,
    };
    const {error} = await api.guests.patch(guestId, payload);
    if (error) {
      const message = getErrorMessage(error);
      console.log('error', message);
    }
  };

  React.useEffect(() => {
    const data = find(obj, ['id', guestId]);
    if (!isEmpty(obj) && !isEmpty(data)) {
      setGuestData(data);
      reset({
        firstName: data.name,
        lastName: data.surname,
        sex: find(GENDERS_OPTIONS(), {value: data.gender}),
        signature: data.signature,
        nationality: {
          value: data.nationality?.code,
          label: data.nationality?.name,
        },
        birthDate: data.birth_date,
        docType: find(getDocTypes(), {value: data.document?.type}),
        docNumber: data.document?.number,
        docDateOfIssue: data.document?.issue_date,
      });
    }
  }, [obj, guestId, reset, t]);

  if (isEmpty(guestData)) {
    return null;
  }

  return (
    <Page>
      <ContractModal
        isOpenModal={isOpenModal}
        setIsOpenModal={setIsOpenModal}
        reservationId={reservationDetails?.reservation?.id}
        housingId={reservationDetails?.reservation?.housing?.id}
      />

      <StyledPage>
        <DesktopBackButton />
        <BackButtonMenu>
          <div className="mobileBackButton" onClick={history.goBack}>
            <StyledIcon name="backArrow" size="16px" />
          </div>
          <div className="backButtonMenuTitle">Edit guest</div>
        </BackButtonMenu>
        <Row isHeaderMenu>
          <HeaderEditGuest>
            <GuestName>Edit {`${guestData?.name} ${guestData?.surname}`}</GuestName>
            <Button
              className="saveButtonHeader"
              label="Save changes"
              type="submit"
              form="hook-form"
            />
          </HeaderEditGuest>
        </Row>
        {isIdentityVerificationEnabled && (
          <>
            <Row>
              <HeaderEditGuest2>{t('identity_verification')}</HeaderEditGuest2>
            </Row>
            <Row>
              <Button className="updateDocument" label="Update document" whiteButton />
            </Row>
            <Row>
              <DocumentPhoto
                photo={fronSideDocumentPicture}
                side={Side.front}
                connectedStatus=" "
                guestId={guestData?.id}
              />
              {backSideDocumentPicture && (
                <DocumentPhoto
                  photo={backSideDocumentPicture}
                  side={Side.back}
                  connectedStatus=" "
                  guestId={guestData?.id}
                />
              )}
              {selfiePicture && (
                <DocumentPhoto
                  photo={selfiePicture}
                  side={Side.selfie}
                  connectedStatus=" "
                  guestId={guestData?.id}
                />
              )}
            </Row>
          </>
        )}
        <Row>
          <form id="hook-form" noValidate onSubmit={handleSubmit(onSubmit)}>
            <Row>
              <HeaderEditGuest2>Guest data and sign</HeaderEditGuest2>
            </Row>
            <Row>
              {/* <DimensionsWrapper> */}
              <StyledForm>
                <Col>
                  <Input type="text" label={t('name')} {...register('firstName')} />
                  <Input {...register('lastName')} type="text" label={t('surname')} />
                  <Controller
                    name="sex"
                    control={control}
                    render={({field, fieldState: {error}}) => (
                      <Select
                        options={GENDERS_OPTIONS()}
                        label={t('sex')}
                        setValue={setValue}
                        error={error?.message}
                        {...field}
                      />
                    )}
                  />
                  <Controller
                    name="nationality"
                    control={control}
                    render={({field, fieldState: {error}}) => (
                      <Select
                        options={locationsAsOptions}
                        label={t('nationality')}
                        setValue={setValue}
                        error={error?.message}
                        {...field}
                      />
                    )}
                  />
                </Col>
                <Col>
                  <Controller
                    name="birthDate"
                    control={control}
                    render={({field: {onChange, ...restField}, fieldState: {error}}) => (
                      <Datepicker
                        label={t('birth_date')}
                        onChange={value => {
                          return onChange(value?.toString());
                        }}
                        error={error?.message}
                        {...restField}
                      />
                    )}
                  />

                  <Controller
                    name="docType"
                    control={control}
                    render={({field, fieldState: {error}}) => (
                      <Select
                        options={getDocTypes()}
                        label={t('doc_type')}
                        setValue={setValue}
                        error={error?.message}
                        {...field}
                      />
                    )}
                  />
                  <Input {...register('docNumber')} type="text" label={t('doc_number')} />
                  <Controller
                    name="docDateOfIssue"
                    control={control}
                    render={({field: {onChange, ...restField}, fieldState: {error}}) => (
                      <Datepicker
                        label={t('date_of_issue')}
                        onChange={value => {
                          return onChange(value?.toString());
                        }}
                        error={error?.message}
                        {...restField}
                      />
                    )}
                  />
                </Col>
                <Col>
                  {!isEmpty(guestData?.signature) && (
                    <Signature>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          marginBottom: '10px',
                        }}
                      >
                        <Button
                          onClick={handleContractButton}
                          icon={<StyledIcon name="magnifierIcon" size="20" />}
                          label="View contract"
                          secondary
                        />
                      </div>
                      <div>
                        <img src={guestData?.signature} alt="Signnature" />
                      </div>
                    </Signature>
                  )}
                </Col>
              </StyledForm>
              {/* </DimensionsWrapper> */}
            </Row>
            <Row style={{padding: '50px 0'}}>
              <Button
                className="saveChangesBottomButton"
                label="Save changes"
                type="submit"
                form="hook-form"
              />
            </Row>
          </form>
        </Row>
      </StyledPage>
    </Page>
  );
}

export default EditRegistredGuest;
